import React from 'react';

export const HelpView = () => {
    return (
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px 20px 80px 20px', color: 'var(--text-color)', height: '100%', overflowY: 'auto' }}>
            <h1 style={{ fontSize: '2rem', marginBottom: '10px' }}>Help & Troubleshooting</h1>
            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '40px' }}>
                Quick guide to common issues and managing your local library.
            </p>

            <section style={{ marginBottom: '40px' }}>
                <h2 style={{ fontSize: '1.4rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>How ClaraNarratio Works</h2>
                <div style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
                    <p>
                        ClaraNarratio is an <strong>offline-first</strong> reader. This means when you download a book, it is stored directly on your device, not on a server.
                    </p>
                    <p>
                        <strong>Important:</strong> Because data is local, "Clearing Site Data" or "Deleting Browsing History" (if including cookies/site data) will <strong>delete all your downloaded books and indexes</strong>.
                    </p>
                </div>
            </section>

            <section style={{ marginBottom: '40px' }}>
                <h2 style={{ fontSize: '1.4rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>Updates & Getting Stuck</h2>
                <div style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
                    <p>
                        The app updates automatically in the background. If you see an "Update available" message, simply click <strong>Reload</strong>.
                    </p>
                    <p>
                        If the app feels "stuck" (blank screen, books not loading, search failing), try these steps in order:
                    </p>
                    <ol style={{ paddingLeft: '20px', margin: '10px 0' }}>
                        <li><strong>Close and Reopen:</strong> Fully close the browser tab or app window and open it again.</li>
                        <li><strong>Hard Refresh:</strong> On Desktop, press <code style={{ background: 'var(--surface-hover)', padding: '2px 4px', borderRadius: '4px' }}>Ctrl + F5</code> (Windows) or <code style={{ background: 'var(--surface-hover)', padding: '2px 4px', borderRadius: '4px' }}>Cmd + Shift + R</code> (Mac).</li>
                        <li><strong>Clear Data:</strong> If problems persist, you likely have corrupted local files. Use the instructions below to clear your data and start fresh.</li>
                    </ol>
                </div>
            </section>

            <section style={{ marginBottom: '40px' }}>
                <h2 style={{ fontSize: '1.4rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>Clear local app data (fixes a crowded ‘My Library’)</h2>
                <div style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>
                    <p>
                        ClaraNarratio is <strong>offline-first</strong>. Your library, reading position, and settings are stored safely on your device, not in the cloud. However, if your library feels "stuck" or crowded with old data, you can reset it.
                    </p>

                    <div style={{ background: 'rgba(255, 100, 100, 0.1)', padding: '15px', borderRadius: '8px', border: '1px solid rgba(255, 100, 100, 0.2)', marginBottom: '20px' }}>
                        <strong style={{ color: 'var(--text-color)' }}>⚠️ Warning: This will delete:</strong>
                        <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                            <li>Your entire "My Library" list.</li>
                            <li>Your current reading position in all books.</li>
                            <li>All downloaded book files and cached indexes.</li>
                            <li>App settings.</li>
                        </ul>
                    </div>

                    <h3 style={{ fontSize: '1.1rem', color: 'var(--text-color)', marginTop: '20px' }}>Step-by-Step Instructions</h3>

                    <p><strong>A) Chrome / Edge (Desktop)</strong></p>
                    <ol style={{ paddingLeft: '20px', margin: '10px 0' }}>
                        <li>Click the lock icon (🔒) in the address bar URL.</li>
                        <li>Select <strong>Site Settings</strong> (or <em>Cookies and site data</em>).</li>
                        <li>Click <strong>Delete data</strong> (or the Trash icon) next to the domain.</li>
                    </ol>

                    <p><strong>B) Chrome (Android)</strong></p>
                    <ol style={{ paddingLeft: '20px', margin: '10px 0' }}>
                        <li>Open the browser menu (⋮) &rarr; <strong>Settings</strong>.</li>
                        <li>Tap <strong>Site Settings</strong> &rarr; <strong>All sites</strong>.</li>
                        <li>Search for "claranarratio", tap it, and tap <strong>Clear & Reset</strong>.</li>
                    </ol>

                    <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                        <em>Note: If you installed ClaraNarratio as an App, you may need to go to Android Settings &rarr; Apps &rarr; ClaraNarratio &rarr; Storage &rarr; Clear Storage.</em>
                    </p>

                    <h3 style={{ fontSize: '1.1rem', color: 'var(--text-color)', marginTop: '20px' }}>After Clearing</h3>
                    <ul style={{ paddingLeft: '20px', margin: '10px 0' }}>
                        <li>✅ <strong>Reload</strong> the page (it will look like a fresh install).</li>
                        <li>✅ Go to <strong>Discover</strong> to find your books again.</li>
                        <li>✅ If you want the curated audiobooks back, simply "Refresh" the page and they will appear in Discover &gt; Featured.</li>
                    </ul>

                    <p style={{ marginTop: '20px', fontSize: '0.9rem', padding: '10px', background: 'var(--surface-hover)', borderRadius: '4px' }}>
                        <strong>Note:</strong> If you see a message saying "Press and hold the Start/Play button...", that is a legacy instruction and should be disregarded/removed.
                    </p>
                </div>
            </section>
        </div>
    );
};
